import { useState, useRef, useEffect } from 'react';
import { useReport } from '../../context/ReportContext';

import { fetchUnifiedAI } from 'dynamicDashboard/utils/apiHooks';
import { AVAILABLE_DATA_SOURCES } from '../../utils/mockDataRegistry';
import { generateWidgetSummaries, WidgetSummary } from '../../utils/widgetSummary';
import { WidgetSelectionMenu } from './WidgetSelectionMenu';
import { ScopeSelector } from './ScopeSelector';
import { useClasses, useSchoolStudents } from 'hooks';
import './AIChatbot.css';

const SCOPE_REF_REGEX = /\{\{(\w+):([^}]+)\}\}/g;

/** Resolve scope ref (type + id) to display label using classes/students lists */
function resolveScopeLabel(type, id, classes, students) {
    if (type === 'class') {
        const c = (classes || []).find((x) => x.value === id);
        return c?.label ?? id;
    }
    if (type === 'student') {
        const s = (students || []).find((x) => String(x.id) === id);
        const first = s?.firstName ?? '';
        const last = s?.lastName ?? '';
        return [first, last].filter(Boolean).join(' ').trim() || id;
    }
    return id;
}

/** Parse inline **bold** in a line and return React nodes */
function renderLineWithBold(line) {
    const parts = line.split('**');
    if (parts.length === 1) return line;
    return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

/** Parse a message line: replace {{type:id}} with scope chips, keep **bold**. Returns React nodes. */
function renderMessageLine(line, classes, students, lineKey) {
    const segments = line.split(/(\{\{\w+:[^}]+\}\})/g);
    return segments.map((seg, i) => {
        const m = seg.match(/^\{\{(\w+):([^}]+)\}\}$/);
        if (m) {
            const [, type, id] = m;
            const label = resolveScopeLabel(type, id, classes, students);
            return (
                <span key={`${lineKey}-${i}`} className='ai-chat-inline-scope'>
                    {label}
                </span>
            );
        }
        return <span key={`${lineKey}-${i}`}>{renderLineWithBold(seg)}</span>;
    });
}

/** Serialize contenteditable DOM to message string with {{type:id}} for scope spans */
function serializeContentEditable(container) {
    if (!container) return '';
    let out = '';
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            out += node.textContent;
            return;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node;
            if (el.getAttribute?.('data-entity-type') && el.getAttribute?.('data-entity-id') != null) {
                out += `{{${el.getAttribute('data-entity-type')}:${el.getAttribute('data-entity-id')}}}`;
                return;
            }
            el.childNodes.forEach(walk);
        }
    };
    container.childNodes.forEach(walk);
    return out;
}

// interface removed
//   id;
//   role: 'user' | 'assistant';
//   content;
//   timestamp;
//   widget?;
//   edit?;
//   dismissed?;
//   isAutomaticProgression?; // Flag to hide automatic progression messages in UI
//   suggestedPrompts?; // Optional suggested prompts from API
// }

const INITIAL_MESSAGE = {
    id: 'initial',
    role: 'assistant',
    content: `Hello! I'm Fae, your AI assistant. I'm here to help answer questions about your school's data and wellbeing insights.

**Please don't include personal or identifiable information** in your messages. Use the **scope selector** below to choose which class or student you're asking about—that keeps your questions focused and your data private.

What would you like to know?`,
    timestamp: new Date(),
};

const SUGGESTIONS = [
    {
        text: 'What is the weekly engagement rate for our students?',
        prompt: 'What is the weekly engagement rate for our students?',
    },
    {
        text: 'How does student readiness compare this week vs last week?',
        prompt: 'How does student readiness compare this week vs last week?',
    },
    {
        text: 'What emotions were the most common this term?',
        prompt: 'What emotions were the most common for students to check-in with this term',
    },
];

// interface removed
//   // Phase 1: Intent Detection Response
//   intent?: 'create' | 'edit' | 'clarify';
//   message;
//   requiresFullContext?;
//   requiresWidgetSelection?;
//   matchingWidgets?: WidgetSummary[];
//
//   // Phase 2: Widget Generation/Edit Response
//   widget?;
//   layout?: GridItem;
//   widgets?: Array<{
//     widget;
//     layout: GridItem;
//   }>;
//   changes?: Record<string, string>;
//
//   // Optional suggested prompts (can be Yes/No buttons or follow-up questions)
//   suggestedPrompts?;
// }

// Legacy EditResponse - kept for backward compatibility during migration
// interface removed
//   widget;
//   layout?: GridItem;
//   message;
//   changes?: Record<string, string>;
// }

export function AIChatbot() {
    const { addWidget, state, updateWidget, updateLayout } = useReport();
    const schoolClasses = useClasses();
    const schoolStudents = useSchoolStudents({ sort: true });
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([INITIAL_MESSAGE]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [error, setError] = useState(null);
    const [pendingWidget, setPendingWidget] = useState(null);
    const [pendingEdit, setPendingEdit] = useState(null);
    const [widgetSelectionMatches, setWidgetSelectionMatches] = useState([]);
    const [currentPhase, setCurrentPhase] = useState('intent');
    const [pendingWidgetId, setPendingWidgetId] = useState(null);
    const pendingWidgetIdRef = useRef(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const [scopePickerOpen, setScopePickerOpen] = useState(false);
    const savedSlashRangeRef = useRef(null);
    const lastKnownRangeRef = useRef(null);
    const conversationIdRef = useRef(null);

    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        const onSelectionChange = () => {
            const sel = window.getSelection();
            const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
            if (range && el.contains(range.commonAncestorContainer)) {
                try {
                    lastKnownRangeRef.current = range.cloneRange();
                } catch (_) {
                    /* cloneRange can throw if range is detached; ignore */
                }
            }
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, [isOpen]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleInputSync = () => {
        const raw = serializeContentEditable(inputRef.current);
        setInputValue(raw);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
            return;
        }
        if (e.key === '@') {
            e.preventDefault();
            const selection = window.getSelection();
            const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
            if (range && inputRef.current?.contains(range.commonAncestorContainer)) {
                savedSlashRangeRef.current = range.cloneRange();
            } else {
                savedSlashRangeRef.current = null;
            }
            setScopePickerOpen(true);
        }
    };

    const handleScopeDismiss = () => {
        const el = inputRef.current;
        const savedRange = savedSlashRangeRef.current;
        savedSlashRangeRef.current = null;
        setScopePickerOpen(false);
        el?.focus();
        if (el && savedRange && el.contains(savedRange.startContainer)) {
            try {
                const text = document.createTextNode('@');
                savedRange.insertNode(text);
                savedRange.setStartAfter(text);
                savedRange.collapse(true);
                const sel = window.getSelection();
                if (sel) {
                    sel.removeAllRanges();
                    sel.addRange(savedRange);
                }
            } catch (err) {
                el.appendChild(document.createTextNode('@'));
            }
            handleInputSync();
        }
    };

    /** Insert scope at saved or current cursor; place cursor after the inserted scope. */
    const handleScopeSelect = (entity) => {
        const el = inputRef.current;
        if (!el) return;
        const selection = window.getSelection();
        const currentRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
        const currentInEl = currentRange && el.contains(currentRange.commonAncestorContainer);
        const savedFromAt = savedSlashRangeRef.current;
        const savedFromAtValid = savedFromAt && el.contains(savedFromAt.startContainer);
        const lastKnown = lastKnownRangeRef.current;
        const lastKnownValid = lastKnown && el.contains(lastKnown.startContainer);
        const insertRange = savedFromAtValid
            ? savedFromAt
            : lastKnownValid
              ? lastKnown
              : currentInEl
                ? currentRange
                : null;

        const span = document.createElement('span');
        span.contentEditable = 'false';
        span.setAttribute('data-entity-type', entity.type);
        span.setAttribute('data-entity-id', entity.id);
        span.className = 'ai-chat-inline-scope';
        span.textContent = entity.label;
        const spaceBefore = document.createTextNode('\u00A0');
        const spaceAfter = document.createTextNode('\u00A0');

        if (insertRange) {
            insertRange.collapse(true);
            insertRange.insertNode(spaceAfter);
            insertRange.collapse(true);
            insertRange.insertNode(span);
            insertRange.collapse(true);
            insertRange.insertNode(spaceBefore);
            insertRange.setStartAfter(spaceAfter);
            insertRange.collapse(true);
            if (selection) {
                selection.removeAllRanges();
                selection.addRange(insertRange);
            }
            lastKnownRangeRef.current = insertRange.cloneRange();
        } else {
            el.appendChild(spaceBefore);
            el.appendChild(span);
            el.appendChild(spaceAfter);
            const endRange = document.createRange();
            endRange.setStartAfter(spaceAfter);
            endRange.collapse(true);
            if (selection) {
                selection.removeAllRanges();
                selection.addRange(endRange);
            }
            lastKnownRangeRef.current = endRange.cloneRange();
        }
        savedSlashRangeRef.current = null;
        el.focus();
        handleInputSync();
    };

    const handleSuggestionClick = (prompt) => {
        if (inputRef.current) {
            inputRef.current.textContent = prompt;
        }
        setInputValue(prompt);
        setTimeout(() => handleSendMessage({ overrideText: prompt }), 100);
    };

    /**
     * Handle clicking on a suggested prompt from a message
     * Removes the suggestion from the message and sends the prompt
     */
    const handleMessageSuggestionClick = (messageId, prompt) => {
        // Remove the suggestion from the message
        setMessages((prev) =>
            prev.map((msg) =>
                msg.id === messageId
                    ? {
                          ...msg,
                          suggestedPrompts: msg.suggestedPrompts?.filter((sp) => sp.prompt !== prompt),
                      }
                    : msg,
            ),
        );

        // Send the prompt
        setInputValue(prompt);
        setTimeout(() => handleSendMessage({ overrideText: prompt }), 100);
    };

    /**
     * Get recent conversation history (last 3-5 messages) for Phase 1
     */
    const getRecentHistory = (count = 5) => {
        const allMessages = messages.filter((m) => m.id !== 'initial');
        return allMessages.slice(-count).map((m) => ({
            role: m.role,
            content: m.content,
        }));
    };

    /**
     * Get full conversation history for Phase 2
     */
    const getFullHistory = () => {
        return messages
            .filter((m) => m.id !== 'initial')
            .map((m) => ({
                role: m.role,
                content: m.content,
            }));
    };

    /**
     * Check if message contains edit keywords (for finding original edit requests)
     */
    const hasEditKeywords = (message) => {
        const editKeywords = ['edit', 'change', 'update', 'modify', 'rename', 'adjust', 'set', 'alter'];
        const lowerMessage = message.toLowerCase();
        return editKeywords.some((keyword) => lowerMessage.includes(keyword));
    };

    const handleSendMessage = async ({ overrideText, phaseOverride, skipUserMessage, intentData } = {}) => {
        const messageText = overrideText || inputValue.trim();
        if (!messageText) return;

        // Add user message unless skipped (e.g., when continuing from widget selection)
        if (!skipUserMessage) {
            const userMessage = {
                id: `msg-${Date.now()}`,
                role: 'user',
                content: messageText,
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, userMessage]);
            setInputValue('');
            if (inputRef.current) {
                inputRef.current.textContent = '';
            }
        }

        setError(null);
        setPendingWidget(null);

        const phase = phaseOverride || currentPhase;

        // Show typing indicator
        setIsTyping(true);

        try {
            // Prepare available data sources for AI context
            const availableDataSources = AVAILABLE_DATA_SOURCES.map((source) => ({
                id: source.id,
                label: source.label,
                description: source.description,
                metricCategory: source.metricCategory,
                dataType: source.dataType,
                availableContexts: source.availableContexts,
                availablePeriods: source.availablePeriods,
                defaultColor: source.defaultColor,
            }));

            // Prepare request payload based on phase
            let requestBody;

            if (phase === 'intent') {
                // Phase 1: Lightweight request with recent history
                // Always include widget summaries for identification and intent detection
                const summaries = generateWidgetSummaries(state.report.widgets, state.report.layout.lg || []);

                requestBody = {
                    message: messageText,
                    conversationHistory: getRecentHistory(),
                    availableDataSources,
                    availableWidgets: summaries, // Always include for context and identification
                    phase: 'intent',
                    conversationId: conversationIdRef.current,
                };
            } else {
                // Phase 2: Full context request
                const summaries = generateWidgetSummaries(state.report.widgets, state.report.layout.lg || []);

                requestBody = {
                    message: messageText,
                    conversationHistory: getFullHistory(),
                    availableDataSources,
                    availableWidgets: summaries, // Include for context even in Phase 2
                    phase: 'execute',
                    mcpToolInfo: {
                        action: intentData?.action,
                        server: intentData?.server,
                        tool: intentData?.tool,
                        arguments: intentData?.arguments,
                    },
                    conversationId: conversationIdRef.current,
                };

                // Include full widget config if editing
                if (pendingWidgetId || pendingWidgetIdRef.current) {
                    const widgetIdToUse = pendingWidgetId || pendingWidgetIdRef.current;
                    const widget = widgetIdToUse ? state.report.widgets[widgetIdToUse] : null;
                    const layout = widgetIdToUse
                        ? state.report.layout.lg?.find((item) => item.i === widgetIdToUse)
                        : null;
                    if (widget) {
                        requestBody.currentWidget = widget;
                        if (layout) {
                            requestBody.currentLayout = layout;
                        }
                    }
                }
            }

            // Call unified AI API
            const response = await fetchUnifiedAI(requestBody);
            const data = response?.result;
            const conversationId = response?.conversation_id;

            if (conversationId && !conversationIdRef.current) conversationIdRef.current = conversationId;

            const sanitiseObj = data?.sanitise_obj;
            // Handle Phase 1 response (intent detection)
            if (phase === 'intent') {
                // // Check if widget selection is required
                // if (data.requiresWidgetSelection && data.matchingWidgets && data.matchingWidgets.length > 0) {
                //   // Show widget selection menu
                //   setWidgetSelectionMatches(data.matchingWidgets);
                //   setCurrentPhase('intent'); // Keep in intent phase until selection

                //   // Create assistant message
                //   const assistantMessage = {
                //     id: `msg-${Date.now() + 1}`,
                //     role: 'assistant',
                //     content: data.message,
                //     timestamp: new Date(),
                //     dismissed: false,
                //     suggestedPrompts: data.suggestedPrompts,
                //   };

                //   setMessages((prev) => [...prev, assistantMessage]);
                //   return;
                // }

                // // Check if full context is required (single widget match or create request)
                // if (data.requiresFullContext) {
                setCurrentPhase('execute');

                // Store pending widget ID if single match found
                if (data.intent === 'edit' && data.matchingWidgets && data.matchingWidgets.length === 1) {
                    setPendingWidgetId(data.matchingWidgets[0].id);
                    pendingWidgetIdRef.current = data.matchingWidgets[0].id;
                }

                // Create assistant message
                const assistantMessage = {
                    id: `msg-${Date.now() + 1}`,
                    role: 'assistant',
                    content: data.message,
                    timestamp: new Date(),
                    dismissed: false,
                    suggestedPrompts: data.suggestedPrompts,
                };

                setMessages((prev) => [...prev, assistantMessage]);

                if (sanitiseObj && sanitiseObj?.has_pii == true) {
                    //Create assistant message
                    const prevPIIMessage = {
                        id: `msg-${Date.now() + 1}`,
                        role: 'assistant',
                        content: sanitiseObj.sanitized_message,
                        timestamp: new Date(),
                        dismissed: false,
                        suggestedPrompts: [],
                    };

                    // setMessages((prev) => [...prev, prevPIIMessage]);
                    setInputValue(sanitiseObj?.sanitized_message);
                    setCurrentPhase('intent');
                    return;
                }

                // Proceed to Phase 2 automatically
                // Skip adding user message since we're continuing automatically
                await handleSendMessage({
                    overrideText: messageText,
                    phaseOverride: 'execute',
                    skipUserMessage: true,
                    intentData: data,
                });
                // Reset phase for next message
                setCurrentPhase('intent');
                return;

                // // Phase 1 response without full context needed (clarification)
                // const assistantMessage: Message = {
                //   id: `msg-${Date.now() + 1}`,
                //   role: 'assistant',
                //   content: data.message,
                //   timestamp: new Date(),
                //   dismissed: false,
                //   suggestedPrompts: data.suggestedPrompts,
                // };

                setMessages((prev) => [...prev, assistantMessage]);

                // Reset phase for next message
                setCurrentPhase('intent');
                return;
            }

            // // Handle Phase 2 response (widget generation/edit)
            // const isMultipleWidgets = !!data.widgets && data.widgets.length > 0;
            // const widgetData = isMultipleWidgets
            //   ? { widgets: data.widgets, message: data.message }
            //   : data.widget && data.layout
            //   ? { widget: data.widget, layout: data.layout, message: data.message }
            //   : null;

            // Create assistant message with widget data
            const assistantMessage = {
                id: `msg-${Date.now() + 1}`,
                role: 'assistant',
                content: data.message,
                timestamp: new Date(),
                // widget: widgetData,
                edit:
                    data.intent === 'edit'
                        ? {
                              widget: data.widget,
                              layout: data.layout,
                              message: data.message,
                              changes: data.changes,
                          }
                        : undefined,
                dismissed: false,
                suggestedPrompts: data.suggestedPrompts,
            };

            setMessages((prev) => [...prev, assistantMessage]);

            // if (widgetData) {
            //   if (data.intent === 'edit') {
            //     setPendingEdit({ widgetId: data.widget.id, widget: data.widget });
            //   } else {
            //     setPendingWidget(widgetData);
            //   }
            // }

            // Reset phase for next interaction
            setCurrentPhase('intent');
            setPendingWidgetId(null);
            pendingWidgetIdRef.current = null;
        } catch (err) {
            console.error('AI API error:', err);
            setError(err.message || 'Failed to communicate with Fae. Please try again.');

            const errorMessage = {
                id: `msg-${Date.now() + 1}`,
                role: 'assistant',
                content: "I'm sorry, I encountered an error while processing your request. Please try again.",
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, errorMessage]);
            setCurrentPhase('intent');
        } finally {
            setIsTyping(false);
        }
    };

    const handleAddWidget = () => {
        if (!pendingWidget) return;

        try {
            // Handle single widget
            if (pendingWidget.widget && pendingWidget.layout) {
                addWidget(pendingWidget.widget, pendingWidget.layout);

                // Mark widget as added in the message
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.widget && msg.widget === pendingWidget
                            ? { ...msg, dismissed: false, widget: undefined }
                            : msg,
                    ),
                );

                // Add confirmation message
                const confirmationMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'assistant',
                    content: 'Element added successfully! You can now see it on your dashboard.',
                    timestamp: new Date(),
                };

                setMessages((prev) => [...prev, confirmationMessage]);
                setPendingWidget(null);
            }
            // Handle multiple widgets
            else if (pendingWidget.widgets && Array.isArray(pendingWidget.widgets)) {
                pendingWidget.widgets.forEach((item) => {
                    addWidget(item.widget, item.layout);
                });

                // Mark widgets as added in the message
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.widget && msg.widget === pendingWidget
                            ? { ...msg, dismissed: false, widget: undefined }
                            : msg,
                    ),
                );

                // Add confirmation message
                const confirmationMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'assistant',
                    content: `Successfully added ${pendingWidget.widgets.length} panel${pendingWidget.widgets.length > 1 ? 's' : ''} to your dashboard!`,
                    timestamp: new Date(),
                };

                setMessages((prev) => [...prev, confirmationMessage]);
                setPendingWidget(null);
            }
        } catch (err) {
            console.error('Error adding widget(s):', err);
            setError('Failed to add element(s). Please try again.');
        }
    };

    const handleDismissWidget = () => {
        if (!pendingWidget) return;

        // Mark the widget message as dismissed
        setMessages((prev) =>
            prev.map((msg) => (msg.widget && msg.widget === pendingWidget ? { ...msg, dismissed: true } : msg)),
        );

        // Clear pending widget
        setPendingWidget(null);

        // Add re-prompt message
        const repromptMessage = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            content:
                'No problem! Would you like me to modify the panel configuration, or would you prefer to create something different?',
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, repromptMessage]);
    };

    /**
     * Handle widget selection from menu
     */
    const handleWidgetSelect = async (widgetId) => {
        const widget = state.report.widgets[widgetId];
        if (!widget) return;

        // Get the original edit request message from conversation history
        // Look for the most recent user message that contains edit keywords
        const editMessages = messages.filter((m) => m.role === 'user').reverse();
        const originalEditMessage = editMessages.find((m) => hasEditKeywords(m.content)) || editMessages[0];

        if (!originalEditMessage) return;

        // Set widget selection state BEFORE calling handleSendMessage
        // Use both state and ref to ensure value is available immediately
        setPendingWidgetId(widgetId);
        pendingWidgetIdRef.current = widgetId;
        setWidgetSelectionMatches([]);
        setCurrentPhase('execute');

        // Create a message that includes the widget selection context
        const selectionMessageText = `Edit "${widget.title || widgetId}" - ${originalEditMessage.content}`;

        // Add a user message indicating which widget was selected
        const selectionMessage = {
            id: `msg-${Date.now()}`,
            role: 'user',
            content: selectionMessageText,
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, selectionMessage]);

        // Proceed to Phase 2 with selected widget
        // Use the selection message text which includes widget context
        // Skip adding another user message since we just added the selection message
        await handleSendMessage({
            overrideText: selectionMessageText,
            phaseOverride: 'execute',
            skipUserMessage: true,
        });

        // Clear the ref after use
        pendingWidgetIdRef.current = null;
    };

    /**
     * Handle widget selection cancellation
     */
    const handleWidgetSelectCancel = () => {
        setWidgetSelectionMatches([]);
        setCurrentPhase('intent'); // Reset to intent phase
        const cancelMessage = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            content: 'No problem! Feel free to ask me to edit an element or panel anytime.',
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, cancelMessage]);
    };

    /**
     * Handle applying edit changes
     */
    const handleApplyEdit = () => {
        if (!pendingEdit) return;

        try {
            // Update widget
            updateWidget(pendingEdit.widgetId, pendingEdit.widget);

            // Update layout if provided
            const lastEditMessage = [...messages].reverse().find((m) => m.edit);
            if (lastEditMessage?.edit?.layout) {
                const layouts = state.report.layout.lg || [];
                const updatedLayouts = layouts.map((item) =>
                    item.i === pendingEdit.widgetId
                        ? (lastEditMessage.edit && lastEditMessage.edit.layout) || item
                        : item,
                );
                updateLayout('lg', updatedLayouts);
            }

            // Clear pending edit
            setPendingEdit(null);

            // Add confirmation message
            const confirmationMessage = {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: 'Panel updated successfully! Your changes have been applied.',
                timestamp: new Date(),
            };

            setMessages((prev) => [...prev, confirmationMessage]);
        } catch (err) {
            console.error('Error applying edit:', err);
            setError('Failed to apply changes. Please try again.');
        }
    };

    /**
     * Handle dismissing edit
     */
    const handleDismissEdit = () => {
        if (!pendingEdit) return;

        // Mark the edit message as dismissed
        setMessages((prev) =>
            prev.map((msg) =>
                msg.edit && msg.edit.widget.id === pendingEdit.widgetId ? { ...msg, dismissed: true } : msg,
            ),
        );

        // Clear pending edit
        setPendingEdit(null);

        // Add re-prompt message
        const repromptMessage = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            content: 'No problem! Would you like to try a different edit, or create something new?',
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, repromptMessage]);
    };

    return (
        <>
            <button
                className={`ai-chatbot-toggle-btn ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title='Fae - AI Report Designer'
            >
                <span className='ai-icon'>✨</span>
            </button>

            {isOpen && (
                <div className='ai-chatbot-panel'>
                    <div className='ai-chatbot-header'>
                        <div className='ai-chatbot-header-left'>
                            <span className='ai-icon'>✨</span>
                            <h3>Fae</h3>
                        </div>
                        <button className='ai-chatbot-close-btn' onClick={() => setIsOpen(false)}>
                            ×
                        </button>
                    </div>

                    <div className='ai-chatbot-messages' id='aiChatbotMessages'>
                        {messages
                            .filter((message) => !message.isAutomaticProgression)
                            .map((message) => (
                                <div
                                    key={message.id}
                                    className={`ai-message ${message.role} ${message.dismissed ? 'dismissed' : ''}`}
                                >
                                    <div className='ai-message-avatar'>
                                        {message.role === 'assistant' ? '🤖' : '👤'}
                                    </div>
                                    <div className='ai-message-content'>
                                        {message.content.split('\n').map((line, i) => (
                                            <div key={i}>
                                                {renderMessageLine(line, schoolClasses, schoolStudents, i)}
                                            </div>
                                        ))}
                                        {message.widget && message.dismissed && (
                                            <div className='widget-dismissed-indicator'>
                                                <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                                                    Element dismissed
                                                </span>
                                            </div>
                                        )}
                                        {message.suggestedPrompts && message.suggestedPrompts.length > 0 && (
                                            <div className='ai-suggestions'>
                                                {message.suggestedPrompts.map((suggestion, i) => (
                                                    <button
                                                        key={i}
                                                        className='ai-suggestion-chip'
                                                        onClick={() =>
                                                            handleMessageSuggestionClick(message.id, suggestion.prompt)
                                                        }
                                                    >
                                                        {suggestion.text}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                        {messages.length === 1 && (
                            <div className='ai-suggestions'>
                                {SUGGESTIONS.map((suggestion, i) => (
                                    <button
                                        key={i}
                                        className='ai-suggestion-chip'
                                        onClick={() => handleSuggestionClick(suggestion.prompt)}
                                    >
                                        {suggestion.text}
                                    </button>
                                ))}
                            </div>
                        )}

                        {isTyping && (
                            <div className='ai-message assistant'>
                                <div className='ai-message-avatar'>🤖</div>
                                <div className='ai-message-content'>
                                    <div className='ai-typing-indicator'>
                                        <div className='ai-typing-dot'></div>
                                        <div className='ai-typing-dot'></div>
                                        <div className='ai-typing-dot'></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className='ai-message assistant'>
                                <div className='ai-message-avatar'>⚠️</div>
                                <div className='ai-message-content' style={{ color: 'var(--color-error)' }}>
                                    {error}
                                </div>
                            </div>
                        )}

                        {widgetSelectionMatches.length > 0 && (
                            <div className='ai-message assistant'>
                                <div className='ai-message-avatar'>🤖</div>
                                <div className='ai-message-content'>
                                    <WidgetSelectionMenu
                                        widgets={widgetSelectionMatches}
                                        onSelect={handleWidgetSelect}
                                        onCancel={handleWidgetSelectCancel}
                                    />
                                </div>
                            </div>
                        )}

                        {pendingEdit && (
                            <div className='ai-message assistant'>
                                <div className='ai-message-avatar'>🤖</div>
                                <div className='ai-message-content'>
                                    <div style={{ marginBottom: 'var(--spacing-md)' }}>
                                        {pendingEdit.widget && (
                                            <>
                                                <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                                                    I've prepared the following changes:
                                                </div>
                                                {(() => {
                                                    const lastEditMessage = [...messages].reverse().find((m) => m.edit);
                                                    const changes = lastEditMessage?.edit?.changes;
                                                    if (changes && Object.keys(changes).length > 0) {
                                                        return (
                                                            <div
                                                                style={{
                                                                    marginBottom: 'var(--spacing-sm)',
                                                                    fontSize: '0.9rem',
                                                                }}
                                                            >
                                                                {Object.entries(changes).map(([key, value]) => (
                                                                    <div key={key} style={{ marginBottom: '4px' }}>
                                                                        <strong>{key}:</strong> {value}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                                Would you like to apply these changes?
                                            </>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleApplyEdit}
                                        style={{
                                            background:
                                                'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: 'var(--spacing-sm) var(--spacing-md)',
                                            borderRadius: 'var(--radius-md)',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        Apply Changes
                                    </button>
                                    <button
                                        onClick={handleDismissEdit}
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid var(--color-border)',
                                            color: 'var(--color-text)',
                                            padding: 'var(--spacing-sm) var(--spacing-md)',
                                            borderRadius: 'var(--radius-md)',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            marginLeft: 'var(--spacing-sm)',
                                        }}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        )}

                        {pendingWidget && (
                            <div className='ai-message assistant'>
                                <div className='ai-message-avatar'>🤖</div>
                                <div className='ai-message-content'>
                                    <div style={{ marginBottom: 'var(--spacing-md)' }}>
                                        {pendingWidget.widgets && Array.isArray(pendingWidget.widgets) ? (
                                            <>
                                                I've prepared {pendingWidget.widgets.length} panel
                                                {pendingWidget.widgets.length > 1 ? 's' : ''} for you. Would you like to
                                                add {pendingWidget.widgets.length > 1 ? 'them' : 'it'} to your
                                                dashboard?
                                            </>
                                        ) : (
                                            <>
                                                I've prepared an element for you. Would you like to add it to your
                                                dashboard?
                                            </>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleAddWidget}
                                        style={{
                                            background:
                                                'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: 'var(--spacing-sm) var(--spacing-md)',
                                            borderRadius: 'var(--radius-md)',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {pendingWidget.widgets && Array.isArray(pendingWidget.widgets)
                                            ? `Add ${pendingWidget.widgets.length} Panel${pendingWidget.widgets.length > 1 ? 's' : ''} to Dashboard`
                                            : 'Add Element to Dashboard'}
                                    </button>
                                    <button
                                        onClick={handleDismissWidget}
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid var(--color-border)',
                                            color: 'var(--color-text)',
                                            padding: 'var(--spacing-sm) var(--spacing-md)',
                                            borderRadius: 'var(--radius-md)',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            marginLeft: 'var(--spacing-sm)',
                                        }}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    <div className='ai-chatbot-input-container'>
                        <div className='ai-chatbot-input-row'>
                            <div className='ai-chatbot-input-wrapper'>
                                <div
                                    ref={inputRef}
                                    className='ai-chatbot-input ai-chatbot-input-editable'
                                    contentEditable
                                    data-placeholder='Ask about your data… Add a class or student scope below to focus your question.'
                                    onInput={handleInputSync}
                                    onKeyDown={handleKeyDown}
                                    suppressContentEditableWarning
                                />
                            </div>
                            <button
                                className='ai-send-btn'
                                onClick={() => handleSendMessage()}
                                disabled={!inputValue.trim()}
                            >
                                <span>Send</span>
                                <span>→</span>
                            </button>
                        </div>
                        <ScopeSelector
                            classes={schoolClasses}
                            students={schoolStudents}
                            onSelectEntity={handleScopeSelect}
                            onDismiss={handleScopeDismiss}
                            disabled={isTyping}
                            open={scopePickerOpen}
                            onOpenChange={setScopePickerOpen}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
